import { setToken } from '@cw-elements/config';
import type { PropsWithChildren } from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import type { Location } from 'react-router';

import { StorageKeyOptionsEnum } from './auth.constant';
import { AuthContext } from './auth.context';
import type {
  AccessDataModel,
  ErrorWithResponse,
  UserDataModel
} from './auth.model';
import { getAccessTokenService, getUser } from './auth.service';
import {
  clearAccessData,
  getLocalData,
  getQueryParams,
  setCookie,
  setLocalData
} from './auth.util';
import { encodeRedirectState } from './utils';

interface AuthProviderProps extends PropsWithChildren {
  clientAuthorizationToken: string;
  redirectUri: string;
  accountsUri: string;
  clientId: string;
  redirect?: (url: string) => void;
}

const AuthProvider = ({
  children,
  clientAuthorizationToken,
  redirectUri,
  accountsUri,
  clientId,
  redirect
}: AuthProviderProps) => {
  const [user, setUser] = useState<UserDataModel | null>(null);
  const exchangedCodes = useRef(new Set<string>());

  const handleUserSignedIn = useCallback(
    (userData: UserDataModel, callback: () => void) => {
      setUser(userData);
      callback();
    },
    []
  );

  const handleRetrieveAccessToken = useCallback(
    async (code: string) => {
      // Authorization codes are single-use; guard against the duplicate
      // exchange that StrictMode / effect re-runs would otherwise trigger
      // (the second attempt returns 400 invalid_grant).
      if (exchangedCodes.current.has(code)) {
        return null;
      }
      exchangedCodes.current.add(code);

      try {
        const accessToken = await getAccessTokenService(
          code,
          clientAuthorizationToken,
          redirectUri,
          clientId
        );

        if (!accessToken) {
          // Release the code so a transient failure stays recoverable.
          exchangedCodes.current.delete(code);
          return null;
        }

        setLocalData(StorageKeyOptionsEnum.AccessData, accessToken);
        setCookie(
          StorageKeyOptionsEnum.AccessData,
          accessToken.access_token,
          1
        );
        // Propagate the freshly acquired token to the cw-elements MFEs (e.g.
        // the topbar profile), which capture the token once at bootstrap and
        // would otherwise stay unauthenticated until a full page reload.
        setToken(accessToken.access_token);

        return accessToken.access_token;
      } catch (error) {
        // Release the code so login can be retried without a full reload.
        exchangedCodes.current.delete(code);
        throw error;
      }
    },
    [clientAuthorizationToken, redirectUri, clientId]
  );

  const handleUrlDirect = useCallback(
    (redirectUrl: string) => {
      if (redirect) {
        redirect(redirectUrl);
        return;
      }
      globalThis.location.href = redirectUrl;
    },
    [redirect]
  );

  const signin = useCallback(
    async (location: Location, callback: VoidFunction) => {
      const params = getQueryParams(location);
      const pathname = location.state?.from?.pathname || '/';
      const search = location.state?.from?.search || '';
      const { code } = params;
      const accessData: AccessDataModel = getLocalData(
        StorageKeyOptionsEnum.AccessData
      );
      let accessToken = accessData ? accessData.access_token : null;

      if (accessData) {
        setCookie(StorageKeyOptionsEnum.AccessData, accessData.access_token, 1);
      }

      const needAccessToken = code && !accessToken;

      if (!accessToken && !needAccessToken) {
        handleUrlDirect(
          `${accountsUri}/oauth/authorize?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}&state=${encodeURIComponent(
            encodeRedirectState(pathname + search)
          )}`
        );
        return;
      }

      if (needAccessToken) {
        accessToken = await handleRetrieveAccessToken(code);
      }

      if (accessToken) {
        try {
          const userData: UserDataModel = await getUser(
            clientAuthorizationToken
          );
          handleUserSignedIn(userData, callback);
        } catch (error: unknown) {
          const err = error as ErrorWithResponse;
          if (
            err.response &&
            (err.response.status === 403 || err.response.status === 401)
          ) {
            clearAccessData();
            handleUrlDirect(
              `${accountsUri}/oauth/authorize?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}`
            );
            return;
          }
        }
      }
    },
    [
      handleRetrieveAccessToken,
      handleUserSignedIn,
      clientAuthorizationToken,
      redirectUri,
      accountsUri,
      clientId,
      handleUrlDirect
    ]
  );

  const signout = useCallback(() => {
    clearAccessData();
    handleUrlDirect(`${accountsUri}/exit?continue=${redirectUri}`);
  }, [accountsUri, redirectUri, handleUrlDirect]);

  const value = useMemo(
    () => ({ user, signin, signout }),
    [user, signin, signout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export { AuthProvider };
